// Reusable toy-like controls. Every control works with mouse, touch, keyboard and gamepad:
// buttons are native <button>s; toggles, sliders, choices and steppers take ←/→ through the
// `ui-adjust` event that the ScreenManager dispatches to the focused element (`data-adjust`).
import { icon } from './icons';
import { clamp, el, setAttr, setStyle, setText } from './ui';

export const ADJUST_EVENT = 'ui-adjust';
export const SPIN_EVENT = 'ui-spin';
export type AdjustEvent = CustomEvent<{ dir: -1 | 1 }>;

export type BtnVariant = 'pink' | 'yellow' | 'cream' | 'mint' | 'sky' | 'grape' | 'danger';

export interface BtnOpts {
  label?: string;
  icon?: string;
  variant?: BtnVariant;
  size?: 'big' | 'small' | 'tiny';
  sub?: string;
  cls?: string;
  title?: string;
  autofocus?: boolean;
  type?: 'button' | 'submit';
  onClick?: (e: MouseEvent) => void;
}

/** A chunky pill button: optional icon, label and a small sub-label. */
export function button(o: BtnOpts): HTMLButtonElement {
  const cls = ['btn', o.variant && o.variant !== 'yellow' ? o.variant : '', o.size ?? '', !o.label ? 'icon' : '', o.cls ?? ''].filter(Boolean).join(' ');
  const b = el('button', {
    class: cls,
    attrs: { type: o.type ?? 'button', 'aria-label': o.label ? undefined : o.title, title: o.title, 'data-autofocus': o.autofocus },
    on: o.onClick ? { click: o.onClick } : undefined,
  });
  if (o.icon) b.append(icon(o.icon));
  if (o.label !== undefined) {
    if (o.sub) b.append(el('span', { class: 'stack' }, el('b', { class: 'lbl', text: o.label, style: { 'font-weight': '400' } }), el('span', { class: 'sub', text: o.sub })));
    else b.append(el('span', { class: 'lbl', text: o.label }));
  }
  return b;
}

/** Sets a button's label text (keeps its icon). */
export function setLabel(b: HTMLElement, text: string): void {
  const lbl = b.querySelector('.lbl');
  if (lbl) setText(lbl, text);
  else setText(b, text);
}

/** Round ✕ close button for a panel corner. */
export function closeButton(onClick: () => void, label = 'Close'): HTMLButtonElement {
  return button({ icon: 'x', title: label, variant: 'cream', size: 'small', cls: 'round close-x', onClick });
}

export interface Control<T> { el: HTMLElement; get(): T; set(v: T, silent?: boolean): void }

function onAdjust(node: HTMLElement, fn: (dir: -1 | 1) => void): void {
  node.setAttribute('data-adjust', '');
  node.addEventListener(ADJUST_EVENT, (e) => fn((e as AdjustEvent).detail.dir));
}

/** A pill switch (role=switch). */
export function toggle(label: string, value: boolean, onChange: (v: boolean) => void): Control<boolean> {
  let v = value;
  const b = el('button', { class: 'toggle', attrs: { type: 'button', role: 'switch', 'aria-label': label, 'aria-checked': String(v) } },
    el('span', { class: 'tlbl on', text: 'ON' }), el('span', { class: 'tlbl off', text: 'OFF' }));
  const set = (nv: boolean, silent = false): void => {
    if (nv === v && silent) return;
    v = nv;
    setAttr(b, 'aria-checked', String(v));
    if (!silent) onChange(v);
  };
  b.addEventListener('click', () => set(!v));
  onAdjust(b, (dir) => set(dir > 0));
  return { el: b, get: () => v, set };
}

export interface SliderOpts { min: number; max: number; step: number; value: number; format?: (v: number) => string; onChange(v: number): void; onCommit?(v: number): void }

/** A chunky slider (role=slider): drag, click, wheel, ←/→ and the gamepad D-pad. */
export function slider(label: string, o: SliderOpts): Control<number> {
  const fmt = o.format ?? ((n: number) => String(n));
  const fill = el('i', { class: 'fill' });
  const knob = el('i', { class: 'knob' });
  const track = el('span', { class: 'track' }, fill, knob);
  const val = el('span', { class: 'val' });
  const root = el('div', { class: 'slider', attrs: { role: 'slider', tabindex: 0, 'aria-label': label, 'aria-valuemin': o.min, 'aria-valuemax': o.max } }, track, val);
  let v = o.value;
  const render = (): void => {
    const k = (v - o.min) / (o.max - o.min);
    setStyle(fill, 'width', `${k * 100}%`);
    setStyle(knob, 'left', `${k * 100}%`);
    setText(val, fmt(v));
    setAttr(root, 'aria-valuenow', String(v));
    setAttr(root, 'aria-valuetext', fmt(v));
  };
  const snap = (x: number): number => {
    const s = Math.round((x - o.min) / o.step) * o.step + o.min;
    return clamp(Number(s.toFixed(4)), o.min, o.max);
  };
  const set = (nv: number, silent = false): void => {
    const s = snap(nv);
    if (s === v) {
      render();
      return;
    }
    v = s;
    render();
    if (!silent) o.onChange(v);
  };
  const fromPointer = (e: PointerEvent): void => {
    const r = track.getBoundingClientRect();
    set(o.min + clamp((e.clientX - r.left) / Math.max(1, r.width), 0, 1) * (o.max - o.min));
  };
  root.addEventListener('pointerdown', (e) => {
    root.setPointerCapture(e.pointerId);
    fromPointer(e);
    e.preventDefault();
    root.focus({ preventScroll: true });
  });
  root.addEventListener('pointermove', (e) => {
    if (root.hasPointerCapture(e.pointerId)) fromPointer(e);
  });
  root.addEventListener('pointerup', () => o.onCommit?.(v));
  root.addEventListener('keydown', (e) => {
    const big = (o.max - o.min) / 10;
    if (e.code === 'Home') set(o.min);
    else if (e.code === 'End') set(o.max);
    else if (e.code === 'PageUp') set(v + big);
    else if (e.code === 'PageDown') set(v - big);
    else return;
    e.preventDefault();
  });
  root.addEventListener('wheel', (e) => {
    if (document.activeElement !== root) return;
    e.preventDefault();
    set(v + (e.deltaY < 0 ? o.step : -o.step));
  }, { passive: false });
  onAdjust(root, (dir) => {
    set(v + dir * o.step);
    o.onCommit?.(v);
  });
  render();
  return { el: root, get: () => v, set };
}

export interface ChoiceOpt<T> { value: T; label: string }

/** A segmented choice (role=radiogroup) with a roving focus: ←/→ change the value. */
export function choice<T>(label: string, options: readonly ChoiceOpt<T>[], value: T, onChange: (v: T) => void): Control<T> {
  let v = value;
  const root = el('div', { class: 'seg', attrs: { role: 'radiogroup', 'aria-label': label } });
  const btns = options.map((opt, i) => {
    const b = el('button', { class: 'opt', text: opt.label, attrs: { type: 'button', role: 'radio', 'aria-checked': 'false', tabindex: -1 } });
    b.addEventListener('click', () => set(options[i].value));
    onAdjust(b, (dir) => {
      const cur = options.findIndex((o) => o.value === v);
      const next = clamp(cur + dir, 0, options.length - 1);
      set(options[next].value);
      btns[next].focus({ preventScroll: true });
    });
    root.append(b);
    return b;
  });
  const render = (): void => {
    btns.forEach((b, i) => {
      const on = options[i].value === v;
      setAttr(b, 'aria-checked', String(on));
      b.tabIndex = on ? 0 : -1;
    });
  };
  const set = (nv: T, silent = false): void => {
    const changed = nv !== v;
    v = nv;
    render();
    if (changed && !silent) onChange(v);
  };
  render();
  return { el: root, get: () => v, set };
}

/** ◀ value ▶ for small integer ranges (bot counts). */
export function stepper(label: string, min: number, max: number, value: number, format: (v: number) => string, onChange: (v: number) => void): Control<number> {
  let v = value;
  const val = el('span', { class: 'val' });
  const dec = button({ icon: 'arrowL', title: `Fewer ${label}`, variant: 'cream', size: 'small', cls: 'round' });
  const inc = button({ icon: 'arrowR', title: `More ${label}`, variant: 'cream', size: 'small', cls: 'round' });
  const root = el('div', { class: 'stepper', attrs: { role: 'spinbutton', 'aria-label': label, tabindex: -1 } }, dec, val, inc);
  const render = (): void => {
    setText(val, format(v));
    setAttr(root, 'aria-valuenow', String(v));
    dec.disabled = v <= min;
    inc.disabled = v >= max;
  };
  const set = (nv: number, silent = false): void => {
    const c = clamp(Math.round(nv), min, max);
    const changed = c !== v;
    v = c;
    render();
    if (changed && !silent) onChange(v);
  };
  dec.addEventListener('click', () => set(v - 1));
  inc.addEventListener('click', () => set(v + 1));
  for (const b of [dec, inc]) onAdjust(b, (dir) => set(v + dir));
  render();
  return { el: root, get: () => v, set };
}

export interface TabItem { id: string; label: string; icon?: string }
export interface Tabs { el: HTMLElement; readonly current: string; select(id: string, focus?: boolean): void; step(dir: -1 | 1): void }

/** A tab strip (role=tablist); LB/RB step through it (the screen forwards `tabL`/`tabR`). */
export function tabs(label: string, items: readonly TabItem[], onSelect: (id: string) => void, padHints = true): Tabs {
  let current = items[0]?.id ?? '';
  const root = el('div', { class: 'tabs', attrs: { role: 'tablist', 'aria-label': label } });
  if (padHints) root.append(el('span', { class: 'key shoulder shoulder-hint', text: 'LB' }));
  const nodes = items.map((it) => {
    const b = el('button', { class: 'tab', attrs: { type: 'button', role: 'tab', 'aria-selected': 'false', 'data-tab': it.id } },
      it.icon ? icon(it.icon) : null, el('span', { class: 'tl', text: it.label }));
    b.addEventListener('click', () => api.select(it.id));
    root.append(b);
    return b;
  });
  if (padHints) root.append(el('span', { class: 'key shoulder shoulder-hint', text: 'RB' }));
  const api: Tabs = {
    el: root,
    get current() {
      return current;
    },
    select(id, focus = false) {
      const i = items.findIndex((t) => t.id === id);
      if (i < 0) return;
      const changed = id !== current;
      current = id;
      nodes.forEach((n, k) => setAttr(n, 'aria-selected', String(k === i)));
      nodes[i].scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
      if (focus) nodes[i].focus({ preventScroll: true });
      if (changed) onSelect(id);
    },
    step(dir) {
      const i = items.findIndex((t) => t.id === current);
      const next = (i + dir + items.length) % items.length;
      // The panel below re-renders, so focus lands on the new tab (never lost to the page).
      api.select(items[next].id, true);
    },
  };
  nodes.forEach((n, k) => setAttr(n, 'aria-selected', String(k === 0)));
  return api;
}

/** A labeled settings row: label (+ hint) on the left, control on the right. */
export function field(label: string, control: HTMLElement, hint?: string): HTMLElement {
  return el('div', { class: 'field' },
    el('div', { class: 'flabel' }, el('span', { text: label }), hint ? el('small', { text: hint }) : null),
    el('div', { class: 'fctl' }, control));
}

/** A sticker panel with an optional ribbon title. */
export function panel(opts: { ribbon?: string; ribbonCls?: string; cls?: string; tag?: 'div' | 'form' | 'section' }, ...children: (Node | null | false)[]): HTMLElement {
  const p = el(opts.tag ?? 'div', { class: `panel${opts.ribbon ? ' with-ribbon' : ''}${opts.cls ? ` ${opts.cls}` : ''}` });
  if (opts.ribbon) p.append(el('div', { class: `ribbon${opts.ribbonCls ? ` ${opts.ribbonCls}` : ''}`, text: opts.ribbon }));
  for (const c of children) if (c) p.append(c);
  return p;
}

/** The bouncing SHOVE OFF! logo, letter by letter. */
export function logo(tagline?: string): HTMLElement {
  const line = (text: string, cls: string): HTMLElement => el('div', { class: `line ${cls}` },
    ...[...text].map((ch) => el('span', { class: `ch${ch === ' ' ? ' sp' : ''}`, text: ch })));
  return el('div', { class: 'logo', attrs: { role: 'img', 'aria-label': 'SHOVE OFF!' } },
    line('SHOVE', 'a'), line('OFF!', 'b'), tagline ? el('div', { class: 'tag', text: tagline }) : null);
}

/** Ping bars: 3 bars, amber over 120 ms, red over 180 ms (§14.2). */
export function pingBars(ping: number): HTMLElement {
  const level = ping <= 80 ? 3 : ping <= 120 ? 2 : 1;
  const cls = ping > 180 ? 'bad' : ping > 120 ? 'warn' : 'good';
  return el('span', { class: `ping ${cls}`, attrs: { title: ping > 0 ? `${Math.round(ping)} ms` : 'ping', 'aria-label': `Ping ${Math.round(ping)} ms` } },
    ...[1, 2, 3].map((i) => el('i', { class: i <= level ? 'on' : '' })));
}
